// Persistence types. Mirrors the on-device schema in
// docs/GAIT_ANALYSIS_ARCHITECTURE.md §12. PHI (video, landmarks, identifiers)
// is local-only; nothing here is shaped for cloud egress.

import type { PoseFrame } from "../pose/landmarks";
import type { GaitEvent, GaitCycle } from "../gait/events";
import type { LegProximity } from "../gait/robustness";
import type { TemporalSummary } from "../gait/temporal";
import type { FrontalFeatures } from "../gait/frontal";
import type { LateralFeatures } from "../gait/lateral";
import type { Finding } from "../gait/bands";
import type { Scores } from "../gait/scoring";
import type { ClinicalMetrics } from "../gait/clinical";
import type { SagittalRom } from "../gait/rom";

export type GaitView = "frontal" | "lateral";

export type Milestone = "baseline" | "6w" | "3m" | "6m" | "12m" | "adhoc";

/** Algorithm version stamps — required compatible for longitudinal comparison. */
export interface Versions {
  poseModel: string;
  smoothing: string;
  feature: string;
  threshold: string;
  scoring: string;
}

export const CURRENT_VERSIONS: Versions = {
  poseModel: "blazepose-full-0.10.18",
  smoothing: "oneeuro+sg-1",
  // phase2-3-robust: adds visibility-gating of low-confidence landmark samples
  // before smoothing/percentiles, plus per-side (near/far-leg) confidence. For
  // high-quality clips this is numerically identical to phase2-2-iso (gating is
  // a no-op when visibility ≥ gate everywhere); occluded far-leg metrics shift
  // toward de-noised values. Recompute from stored landmarks to migrate a trend
  // across this bump.
  feature: "phase2-3-robust",
  // phase2-seed-2: frontal x-based seed thresholds rescaled to isotropic
  // (square-pixel) units (× 9/16) so banding is aspect-independent after the
  // phase2-2-iso geometry fix. y-based / ratio thresholds unchanged.
  threshold: "phase2-seed-2",
  // phase2-2: frontal x-based scoring references rescaled in step with seed-2.
  scoring: "phase2-2",
};

export interface Patient {
  id: string; // local uuid — never synced
  code: string; // clinician-facing label / code (no PHI required)
  heightCm?: number; // for spatial calibration + height-adjusted norms
  sex?: "F" | "M" | "X";
  birthYear?: number;
  createdAt: number;
  updatedAt: number;
}

export interface QcSnapshot {
  passed: boolean;
  fullBodyRatio: number;
  meanConfidence: number;
  fps: number;
  detectedCycles: number;
  /**
   * Actionable, plain-language reasons the capture didn't pass QC (e.g.
   * "improve lighting, use shorts, plain background"), as produced by
   * `runQc()`. Optional for backward compatibility with sessions stored
   * before this field existed.
   */
  warnings?: string[];
}

export interface Session {
  id: string;
  patientId: string;
  view: GaitView;
  milestone: Milestone;
  recordedAt: number;
  videoRef: string | null; // Filesystem URI (local only)
  fps: number;
  frameCount: number;
  durationMs: number;
  qc: QcSnapshot;
  versions: Versions;
  createdAt: number;
  /**
   * Distinguishes a Flex (fitness/wellness) session from a Gait session.
   * Optional + defaults to "gait" for backward compatibility with sessions
   * stored before this field existed. See gait/docs/FLEX_WELLNESS_ABGRENZUNG.md
   * — Flex never reuses gait's clinical AnalysisResult; its own result lives
   * in flex/types.ts's FlexResult, stored separately (see store/db.ts).
   */
  testType?: "gait" | "flex";
}

/** Large, local-only landmark stream (MVP stores JSON; Phase 2 packs to 16-bit). */
export interface LandmarkStream {
  sessionId: string;
  schemaVersion: number;
  frames: PoseFrame[];
}

export interface AnalysisResult {
  sessionId: string;
  events: GaitEvent[];
  cycles: GaitCycle[];
  temporal: TemporalSummary;
  direction: number;
  /** Phase 2: categorical findings, scores, and raw feature sets. */
  findings?: Finding[];
  scores?: Scores;
  frontal?: FrontalFeatures;
  lateral?: LateralFeatures;
  /** Phase 2a: extended clinical metrics + estimated sagittal ROM. */
  clinical?: ClinicalMetrics;
  rom?: SagittalRom;
  /** Steady-state walking segment that was analysed (ms into the clip). */
  boutMs?: { startMs: number; endMs: number };
  /** Advisory if the recording likely doesn't match the selected view. */
  viewAdvisory?: string | null;
  /**
   * Near/far-leg split for a lateral clip: the camera-side leg is more reliable
   * than the occluded far leg. Drives per-side confidence; lets the UI flag
   * which side's metrics to read more cautiously. Undefined for frontal clips.
   */
  legProximity?: LegProximity;
}
